// ─────────────────────────────────────────────────────────────────
// Glass-i — Account Reports API client (Stage 6B.5).
//
// Thin axios wrappers around the 4 read-only report endpoints under
// /api/erp/accounts/reports/*. All endpoints accept query params and
// return the standard `{success, data, requestId}` envelope; this
// client unwraps to `data` for consumers.
//
// Also re-exports `ledgerLookup` from the voucher API so the Ledger
// Statement page can use the same searchable dropdown as the Journal
// Voucher form without duplicating the wiring.
// ─────────────────────────────────────────────────────────────────

import client from '../../../../api/client';
import { voucherApi } from '../vouchers/voucherApi';

const BASE = '/api/erp/accounts/reports';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

// Strip null / undefined / empty-string params so the query string
// stays clean. axios passes through `undefined` but renders `null`
// as the literal string "null" — neither is what the backend wants.
function cleanParams(p) {
  const out = {};
  if (!p) return out;
  for (const [k, v] of Object.entries(p)) {
    if (v === null || v === undefined || v === '') continue;
    out[k] = v;
  }
  return out;
}

export const accountReportsApi = {
  trialBalance: (params) =>
    client.get(`${BASE}/trial-balance`, { params: cleanParams(params) }).then(unwrap),
  ledgerStatement: (params) =>
    client.get(`${BASE}/ledger-statement`, { params: cleanParams(params) }).then(unwrap),
  voucherRegister: (params) =>
    client.get(`${BASE}/voucher-register`, { params: cleanParams(params) }).then(unwrap),
  accountBalances: (params) =>
    client.get(`${BASE}/account-balances`, { params: cleanParams(params) }).then(unwrap),

  // Stage 6B.7E — Profit & Loss + Balance Sheet (read-only).
  profitLoss: (params) =>
    client.get(`${BASE}/profit-loss`, { params: cleanParams(params) }).then(unwrap),
  balanceSheet: (params) =>
    client.get(`${BASE}/balance-sheet`, { params: cleanParams(params) }).then(unwrap),

  // Stage 6B.9G — Fixed Asset / Investment Schedule (read-only).
  fixedAssetSchedule: (params) =>
    client.get(`${BASE}/fixed-asset-schedule`, { params: cleanParams(params) }).then(unwrap),
  investmentSchedule: (params) =>
    client.get(`${BASE}/investment-schedule`, { params: cleanParams(params) }).then(unwrap),

  // Stage 6E.3 — Debtors / Creditors / Party-detail aging (read-only).
  debtorsAging: (params) =>
    client.get(`${BASE}/debtors-aging`, { params: cleanParams(params) }).then(unwrap),
  creditorsAging: (params) =>
    client.get(`${BASE}/creditors-aging`, { params: cleanParams(params) }).then(unwrap),
  agingDetail: (params) =>
    client.get(`${BASE}/aging-detail`, { params: cleanParams(params) }).then(unwrap),

  // Re-export the existing ledger lookup so consumers don't depend on
  // the voucher module directly.
  ledgerLookup: (q, limit = 50) => voucherApi.ledgerLookup(q, limit)
};

export default accountReportsApi;
